import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

interface PremiumUser {
  user_id: number;
  name: string;
  email: string;
  is_premium: boolean;
  premium_source: string | null;
  premium_expires_at: string | null;
  current_plan: string | null;
  subscription_start: string | null;
  subscription_end: string | null;
  coin_balance: number;
  total_subscriptions: number;
}

interface SubscriptionPlan {
  id: number;
  name: string;
  duration_days: number;
  price_inr: number;
  coins_cost: number | null;
}

export default function PremiumUsers() {
  const [users, setUsers] = useState<PremiumUser[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<PremiumUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'expired'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<PremiumUser | null>(null);
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [showCoinsModal, setShowCoinsModal] = useState(false);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [coinsAction, setCoinsAction] = useState<'add' | 'subtract' | 'set'>('add');
  const [coinsAmount, setCoinsAmount] = useState('');
  const [coinsReason, setCoinsReason] = useState('');

  const getToken = useCallback(() => localStorage.getItem('admin_token'), []);

  const loadPremiumUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/premium-users`,
        {
          headers: { Authorization: `Bearer ${getToken()}` },
        }
      );
      if (response.data.success) {
        setUsers(response.data.data.users);
      }
    } catch (error) {
      console.error('Error loading premium users:', error);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  const loadPlans = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/subscription-plans`,
        {
          headers: { Authorization: `Bearer ${getToken()}` },
        }
      );
      if (response.data.success) {
        setPlans(response.data.data.plans.filter((p: SubscriptionPlan & { is_active: boolean }) => p.is_active));
      }
    } catch (error) {
      console.error('Error loading plans:', error);
    }
  }, [getToken]);

  const filterUsers = useCallback(() => {
    let filtered = users;

    // Filter by status
    if (statusFilter === 'active') {
      filtered = filtered.filter(u => u.is_premium);
    } else if (statusFilter === 'expired') {
      filtered = filtered.filter(u => !u.is_premium);
    }

    // Filter by search
    if (searchQuery) {
      filtered = filtered.filter(
        u =>
          u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          u.email.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredUsers(filtered);
  }, [users, statusFilter, searchQuery]);

  const handleRevoke = async (userId: number, userName: string) => {
    if (!window.confirm(`Are you sure you want to revoke premium for ${userName}? This will immediately expire their subscription.`)) {
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/admin/premium-users/${userId}/revoke`,
        { reason: 'Admin revoked' },
        {
          headers: { Authorization: `Bearer ${getToken()}` },
        }
      );
      if (response.data.success) {
        alert('Premium membership revoked successfully');
        loadPremiumUsers();
      }
    } catch (error) {
      console.error('Error revoking premium:', error);
      alert('Failed to revoke premium');
    }
  };

  const handleGrantPremium = async () => {
    if (!selectedUser || !selectedPlanId) {
      alert('Please select a plan');
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/admin/premium-users/${selectedUser.user_id}/grant`,
        { 
          plan_id: selectedPlanId,
          reason: 'Admin granted premium'
        },
        {
          headers: { Authorization: `Bearer ${getToken()}` },
        }
      );
      if (response.data.success) {
        alert('Premium granted successfully');
        setShowGrantModal(false);
        setSelectedUser(null);
        setSelectedPlanId(null);
        loadPremiumUsers();
      }
    } catch (error) {
      console.error('Error granting premium:', error);
      alert('Failed to grant premium');
    }
  };

  const handleAdjustCoins = async () => {
    if (!selectedUser || !coinsAmount) {
      alert('Please enter an amount');
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/admin/premium-users/${selectedUser.user_id}/adjust-coins`,
        {
          action: coinsAction,
          amount: parseInt(coinsAmount),
          reason: coinsReason || `Admin ${coinsAction}`
        },
        {
          headers: { Authorization: `Bearer ${getToken()}` },
        }
      );
      if (response.data.success) {
        alert(`Coins ${coinsAction}ed successfully`);
        setShowCoinsModal(false);
        setSelectedUser(null);
        setCoinsAmount('');
        setCoinsReason('');
        loadPremiumUsers();
      }
    } catch (error) {
      console.error('Error adjusting coins:', error);
      alert('Failed to adjust coins');
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusBadge = (user: PremiumUser) => {
    if (user.is_premium) {
      return <span className="badge bg-success">Active</span>;
    } else {
      return <span className="badge bg-secondary">Expired</span>;
    }
  };

  useEffect(() => {
    loadPremiumUsers();
    loadPlans();
  }, [loadPremiumUsers, loadPlans]);

  useEffect(() => {
    filterUsers();
  }, [filterUsers]);

  // Calculate CRM statistics
  const totalUsers = users.length;
  const activePremium = users.filter(u => u.is_premium).length;
  const expiredPremium = users.filter(u => !u.is_premium).length;
  const totalCoins = users.reduce((sum, u) => sum + u.coin_balance, 0);
  const avgCoins = totalUsers > 0 ? Math.round(totalCoins / totalUsers) : 0;
  if (loading) {
    return (
      <div className="container-fluid p-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">Premium Users Management</h2>
      </div>

      {/* CRM Statistics Cards */}
      <div className="row mb-4">
        <div className="col-md-3">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <div className="bg-primary bg-opacity-10 rounded-circle p-3">
                    <i className="bx bx-user fs-4 text-primary"></i>
                  </div>
                </div>
                <div className="flex-grow-1 ms-3">
                  <h6 className="text-muted mb-1 small">Total Premium</h6>
                  <h4 className="mb-0 fw-bold text-primary">{totalUsers}</h4>
                  <span className="text-muted small">Premium users</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <div className="bg-success bg-opacity-10 rounded-circle p-3">
                    <i className="bx bx-check-circle fs-4 text-success"></i>
                  </div>
                </div>
                <div className="flex-grow-1 ms-3">
                  <h6 className="text-muted mb-1 small">Active</h6>
                  <h4 className="mb-0 fw-bold text-success">{activePremium}</h4>
                  <span className="text-muted small">Current subscriptions</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <div className="bg-warning bg-opacity-10 rounded-circle p-3">
                    <i className="bx bx-coin fs-4 text-warning"></i>
                  </div>
                </div>
                <div className="flex-grow-1 ms-3">
                  <h6 className="text-muted mb-1 small">Avg Coins</h6>
                  <h4 className="mb-0 fw-bold text-warning">{avgCoins.toLocaleString()}</h4>
                  <span className="text-muted small">Per user</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <div className="bg-secondary bg-opacity-10 rounded-circle p-3">
                    <i className="bx bx-time-five fs-4 text-secondary"></i>
                  </div>
                </div>
                <div className="flex-grow-1 ms-3">
                  <h6 className="text-muted mb-1 small">Expired</h6>
                  <h4 className="mb-0 fw-bold text-secondary">{expiredPremium}</h4>
                  <span className="text-muted small">Past subscriptions</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <input
                type="text"
                className="form-control"
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <select
                className="form-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
              >
                <option value="all">All Users</option>
                <option value="active">Active Premium</option>
                <option value="expired">Expired Premium</option>
              </select>
            </div>
            <div className="col-md-4">
              <div className="text-muted">
                Showing {filteredUsers.length} of {users.length} users
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="card">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Plan</th>
                  <th>Start Date</th>
                  <th>Expires</th>
                  <th>Status</th>
                  <th>Coins</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.user_id}>
                    <td>{user.user_id}</td>
                    <td>{user.name}</td>
                    <td>{user.email}</td>
                    <td>{user.current_plan || 'N/A'}</td>
                    <td>{formatDate(user.subscription_start)}</td>
                    <td>{formatDate(user.subscription_end)}</td>
                    <td>{getStatusBadge(user)}</td>
                    <td>{user.coin_balance}</td>
                    <td>
                      <div className="btn-group btn-group-sm">
                        {user.is_premium && (
                          <button
                            className="btn btn-danger"
                            onClick={() => handleRevoke(user.user_id, user.name)}
                          >
                            Revoke
                          </button>
                        )}
                        <button
                          className="btn btn-success"
                          onClick={() => {
                            setSelectedUser(user);
                            setShowGrantModal(true);
                          }}
                        >
                          Grant
                        </button>
                        <button
                          className="btn btn-warning"
                          onClick={() => {
                            setSelectedUser(user);
                            setShowCoinsModal(true);
                          }}
                        >
                          Coins
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Grant Premium Modal */}
      {showGrantModal && selectedUser && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Grant Premium to {selectedUser.name}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowGrantModal(false);
                    setSelectedUser(null);
                    setSelectedPlanId(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Select Plan</label>
                  <select
                    className="form-select"
                    value={selectedPlanId || ''}
                    onChange={(e) => setSelectedPlanId(parseInt(e.target.value))}
                  >
                    <option value="">Choose a plan...</option>
                    {plans.map((plan) => (
                      <option key={plan.id} value={plan.id}>
                        {plan.name} - {plan.duration_days} days (₹{plan.price_inr})
                      </option>
                    ))}
                  </select>
                </div>
                {selectedPlanId && (
                  <div className="alert alert-info">
                    This will grant {selectedUser.name} premium access for the selected plan duration.
                    Any existing premium will be replaced.
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowGrantModal(false);
                    setSelectedUser(null);
                    setSelectedPlanId(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleGrantPremium}
                  disabled={!selectedPlanId}
                >
                  Grant Premium
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Adjust Coins Modal */}
      {showCoinsModal && selectedUser && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Adjust Coins for {selectedUser.name}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowCoinsModal(false);
                    setSelectedUser(null);
                    setCoinsAmount('');
                    setCoinsReason('');
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Current Balance</label>
                  <input
                    type="text"
                    className="form-control"
                    value={selectedUser.coin_balance}
                    disabled
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Action</label>
                  <div>
                    <div className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="coinsAction"
                        id="addCoins"
                        checked={coinsAction === 'add'}
                        onChange={() => setCoinsAction('add')}
                      />
                      <label className="form-check-label" htmlFor="addCoins">
                        Add Coins
                      </label>
                    </div>
                    <div className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="coinsAction"
                        id="subtractCoins"
                        checked={coinsAction === 'subtract'}
                        onChange={() => setCoinsAction('subtract')}
                      />
                      <label className="form-check-label" htmlFor="subtractCoins">
                        Subtract Coins
                      </label>
                    </div>
                    <div className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="coinsAction"
                        id="setCoins"
                        checked={coinsAction === 'set'}
                        onChange={() => setCoinsAction('set')}
                      />
                      <label className="form-check-label" htmlFor="setCoins">
                        Set Exact Amount
                      </label>
                    </div>
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label">Amount</label>
                  <input
                    type="number"
                    className="form-control"
                    value={coinsAmount}
                    onChange={(e) => setCoinsAmount(e.target.value)}
                    placeholder="Enter amount"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Reason (Optional)</label>
                  <input
                    type="text"
                    className="form-control"
                    value={coinsReason}
                    onChange={(e) => setCoinsReason(e.target.value)}
                    placeholder="Enter reason for adjustment"
                  />
                </div>
                {coinsAmount && (
                  <div className="alert alert-info">
                    {coinsAction === 'add' && `New Balance: ${selectedUser.coin_balance + parseInt(coinsAmount)}`}
                    {coinsAction === 'subtract' && `New Balance: ${Math.max(0, selectedUser.coin_balance - parseInt(coinsAmount))}`}
                    {coinsAction === 'set' && `New Balance: ${parseInt(coinsAmount)}`}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowCoinsModal(false);
                    setSelectedUser(null);
                    setCoinsAmount('');
                    setCoinsReason('');
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleAdjustCoins}
                  disabled={!coinsAmount}
                >
                  Update Coins
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

